import React from 'react';
import {useMatches, useLocation} from '@remix-run/react';
import type {I18nLocale} from '~/lib/type';
import t2sMap from '~/data/t2sMap.json';

/**
 * Renders nothing; on the /zh-cn locale it converts all visible Traditional
 * Chinese text in the DOM to Simplified, client-side, after hydration.
 *
 * Traditional→Simplified is (almost entirely) many-to-one at the character
 * level, so a single-character map from OpenCC's TSCharacters dictionary is
 * lossless here — no phrase dictionary or heavy runtime library needed. This
 * lets the store serve Simplified without duplicating any content: pages are
 * authored once in Traditional (Sanity zh_TW) and converted on the fly.
 *
 * SSR paints Traditional and this converts after mount, so there is no
 * hydration mismatch (server and first client render agree).
 */

const MAP = t2sMap as Record<string, string>;
const SKIP_TAGS = new Set([
  'SCRIPT',
  'STYLE',
  'NOSCRIPT',
  'TEXTAREA',
  'CODE',
  'PRE',
]);

function convertString(input: string): string | null {
  let out = '';
  let changed = false;
  for (const ch of input) {
    const s = MAP[ch];
    if (s) {
      out += s;
      changed = true;
    } else {
      out += ch;
    }
  }
  return changed ? out : null;
}

function shouldSkip(node: Node | null): boolean {
  let el = node?.parentElement ?? null;
  while (el) {
    if (SKIP_TAGS.has(el.tagName)) return true;
    if (el.getAttribute && el.getAttribute('translate') === 'no') return true;
    el = el.parentElement;
  }
  return false;
}

function convertTextNode(node: Text) {
  if (!node.nodeValue || shouldSkip(node)) return;
  const next = convertString(node.nodeValue);
  if (next != null) node.nodeValue = next;
}

function convertTree(root: Node) {
  if (root.nodeType === Node.TEXT_NODE) {
    convertTextNode(root as Text);
    return;
  }
  if (root.nodeType !== Node.ELEMENT_NODE) return;
  const el = root as Element;
  if (SKIP_TAGS.has(el.tagName)) return;
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  let n = walker.nextNode();
  while (n) {
    nodes.push(n as Text);
    n = walker.nextNode();
  }
  nodes.forEach(convertTextNode);
}

export default function SimplifiedChinese() {
  const [root] = useMatches();
  const selectedLocale = root.data?.selectedLocale as I18nLocale | undefined;
  // The /zh-cn locale keeps language ZH_TW (so all data behaves like the
  // Traditional site); Simplified is signalled purely by the path prefix.
  const isZhCn = selectedLocale?.pathPrefix === '/zh-cn';
  const {pathname} = useLocation();

  React.useEffect(() => {
    if (!isZhCn || typeof document === 'undefined') return;

    document.documentElement.setAttribute('lang', 'zh-Hans');

    let observer: MutationObserver | null = null;
    const run = () => {
      observer?.disconnect();
      convertTree(document.body);
      observer?.observe(document.body, {
        subtree: true,
        childList: true,
        characterData: true,
      });
    };

    observer = new MutationObserver((mutations) => {
      observer?.disconnect();
      for (const m of mutations) {
        if (m.type === 'characterData') {
          convertTextNode(m.target as Text);
        } else {
          m.addedNodes.forEach((node) => convertTree(node));
        }
      }
      observer?.observe(document.body, {
        subtree: true,
        childList: true,
        characterData: true,
      });
    });

    run();
    return () => observer?.disconnect();
  }, [isZhCn, pathname]);

  return null;
}
